import { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { findByPerfil } from "../../Usuario/api-usuario-perfil";
import { PROFESSOR } from "../../Usuario/Perfil";
import { findBySelectedYearAndTdpUsuarioId, findBySelectedYearAndTdpUsuarioIdVersao2 } from "../api-cronograma";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import Loading from "../../UI/Loading";
import { mensagemDe } from "../cronogramaUtil";
import CronogramaProfessorTable from "./CronogramaProfessorTable";
import CronogramaProfessorTableV2 from "./CronogramaProfessorTableV2";

const LISTA = "lista";
const GRADE = "grade";

const CronogramaProfessorContainer = () => {
  const [professores, setProfessores] = useState([]);
  const [isLoadingProfessores, setIsLoadingProfessores] = useState(true);
  const [professor, setProfessor] = useState(null);
  const [visao, setVisao] = useState(LISTA);
  const [cronogramas, setCronogramas] = useState([]);
  const [isLoadingCronogramas, setIsLoadingCronogramas] = useState(false);
  const codProfessor = professor?.id;

  useEffect(() => {
    findByPerfil(PROFESSOR)
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(mensagemDe(data));
        } else if (Array.isArray(data)) {
          setProfessores(
            data
              .map((up) => up.usuario)
              .filter(Boolean)
              .sort((a, b) => (a.nome ?? "").localeCompare(b.nome ?? "", "pt-BR")),
          );
        }
      })
      .finally(() => setIsLoadingProfessores(false));
  }, []);

  useEffect(() => {
    setCronogramas([]);
    if (!codProfessor) return undefined;
    let active = true;
    setIsLoadingCronogramas(true);
    const request =
      visao === GRADE
        ? findBySelectedYearAndTdpUsuarioIdVersao2(codProfessor)
        : findBySelectedYearAndTdpUsuarioId(codProfessor);
    request
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(mensagemDe(data));
        } else if (Array.isArray(data)) {
          setCronogramas(data);
        }
      })
      .finally(() => {
        if (active) setIsLoadingCronogramas(false);
      });
    return () => {
      active = false;
    };
  }, [codProfessor, visao]);

  const renderConteudo = () => {
    if (!professor) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <PersonSearchOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Selecione um(a) professor(a) para ver o cronograma.
          </Typography>
        </Box>
      );
    }
    if (isLoadingCronogramas) {
      return (
        <Box py={6}>
          <Loading description="Carregando cronograma..." />
        </Box>
      );
    }
    return visao === GRADE ? (
      <CronogramaProfessorTableV2 cronogramas={cronogramas} />
    ) : (
      <CronogramaProfessorTable cronogramas={cronogramas} />
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        mb={2}
        className="no-print"
      >
        <Autocomplete
          options={professores}
          value={professor}
          loading={isLoadingProfessores}
          onChange={(event, value) => setProfessor(value)}
          getOptionLabel={(option) => option.nome ?? ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="Nenhum professor encontrado"
          loadingText="Carregando professores..."
          renderInput={(params) => <TextField {...params} size="small" label="Professor(a)" />}
          sx={{ minWidth: 300, flexGrow: { md: 0 } }}
        />
        <ToggleButtonGroup size="small" exclusive value={visao} onChange={(event, value) => value && setVisao(value)}>
          <ToggleButton value={LISTA}>Lista</ToggleButton>
          <ToggleButton value={GRADE}>Grade semanal</ToggleButton>
        </ToggleButtonGroup>
        <Box flexGrow={1} />
        {professor && visao === GRADE && (
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()}>
            Imprimir
          </Button>
        )}
      </Stack>

      {professor && (
        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
          {professor.nome}
        </Typography>
      )}

      {renderConteudo()}
    </Box>
  );
};

export default CronogramaProfessorContainer;
